import { useEffect, useRef, useState } from 'react';
import { GameTable } from './components/GameTable';
import { LanguagePicker } from './components/LanguagePicker';
import { LeaveConfirm } from './components/LeaveConfirm';
import { Lobby } from './components/Lobby';
import { NicknameGate } from './components/NicknameGate';
import { RoomLobby } from './components/RoomLobby';
import { ThemePicker } from './components/ThemePicker';
import { Toasts } from './components/Toasts';
import { useAppState, useCardTheme, useLocale, useT } from './hooks';
import { LOCALES } from './i18n';
import { net } from './net';

export function App() {
  const state = useAppState();
  const t = useT();
  const [locale, setLocale] = useLocale();
  const [theme, setTheme] = useCardTheme();
  const [showThemes, setShowThemes] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Keep the address bar in sync so a room is shareable and refresh-safe. Only
  // clear it once we have actually left a room — wiping it on mount would throw
  // away the very deep link we are in the middle of joining.
  const wasInRoom = useRef(false);
  const roomId = state.room?.id ?? null;
  useEffect(() => {
    if (roomId) {
      wasInRoom.current = true;
      if (location.pathname !== `/r/${roomId}`) history.replaceState(null, '', `/r/${roomId}`);
    } else if (wasInRoom.current) {
      wasInRoom.current = false;
      history.replaceState(null, '', '/');
    }
  }, [roomId]);

  // First-time visitors are asked to name themselves before anything else.
  const needsNickname = !state.hasChosenName || editingName;
  const phase = state.room?.game.phase;
  const inGame = !!state.room && phase !== 'LobbyWaiting' && phase !== 'GameOver';

  // Closing the tab mid-round forfeits, so let the browser ask first. Refreshing
  // is safe (the session reconnects), but the browser cannot tell them apart.
  useEffect(() => {
    if (!inGame) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = t.leave.unload;
      return t.leave.unload;
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [inGame, t.leave.unload]);

  if (state.status === 'connecting' && !state.sessionId) {
    return (
      <div className="app app--center">
        <div className="spinner" />
        <p className="muted">{t.conn.connectingFull}</p>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="topbar">
        <button
          type="button"
          className="brand"
          onClick={() => {
            if (!state.room) return;
            if (inGame) setConfirmLeave(true);
            else net.leaveRoom();
          }}
          title={state.room ? t.common.leave : 'UNO Online'}
        >
          <span className="brand__mark">UNO</span>
          <span className="brand__text">Online</span>
        </button>

        <div className="topbar__right">
          <span className={`conn conn--${state.status}`}>
            <i className="conn__dot" />
            <span className="conn__label">
              {state.status === 'online' ? t.conn.live : state.status === 'connecting' ? t.conn.connecting : t.conn.offline}
            </span>
          </span>
          <div className="topbar__chips">
            <button
              type="button"
              className="chip"
              onClick={() => setShowLanguages(true)}
              title={t.language.title}
            >
              🌐 <span className="chip__label">{LOCALES[locale].meta.name}</span>
            </button>
            <button type="button" className="chip" onClick={() => setShowThemes(true)} title={t.theme.title}>
              🎴 <span className="chip__label">{theme.name}</span>
            </button>
            <button type="button" className="chip" onClick={() => setEditingName(true)}>
              {state.emoji || '👤'} <span className="chip__label">{state.nickname || t.nickname.pickTitle}</span>
            </button>
          </div>

          {/* Narrow screens: the same three settings, behind a drawer. */}
          <button
            type="button"
            className="burger"
            onClick={() => setMenuOpen(true)}
            aria-label={t.common.menu}
            aria-expanded={menuOpen}
          >
            ☰
          </button>
        </div>
      </header>

      <main className="main">
        {state.room ? (
          state.room.game.phase === 'LobbyWaiting' ? (
            <RoomLobby room={state.room} theme={theme} />
          ) : (
            <GameTable room={state.room} theme={theme} pending={state.pending} feed={state.feed} />
          )
        ) : (
          <Lobby rooms={state.rooms} joinError={state.joinError} passwordPromptFor={state.passwordPromptFor} />
        )}
      </main>

      {state.status === 'offline' && (
        <div className="reconnect-banner" role="status">
          {t.conn.reconnectBanner}
        </div>
      )}

      {needsNickname && (
        <NicknameGate
          initial={state.nickname}
          initialEmoji={state.emoji}
          firstRun={!state.hasChosenName}
          onSubmit={(name, emoji) => {
            net.setProfile(name, emoji);
            setEditingName(false);
          }}
          onCancel={state.hasChosenName ? () => setEditingName(false) : undefined}
        />
      )}

      {showThemes && <ThemePicker current={theme} onSelect={setTheme} onClose={() => setShowThemes(false)} />}

      {showLanguages && (
        <LanguagePicker current={locale} onSelect={setLocale} onClose={() => setShowLanguages(false)} />
      )}

      {menuOpen && (
        <div className="drawer-backdrop" onClick={() => setMenuOpen(false)}>
          <nav className="drawer" onClick={(e) => e.stopPropagation()} aria-label={t.common.menu}>
            <p className="drawer__title">{t.common.menu}</p>

            <button
              type="button"
              className="drawer__row"
              onClick={() => {
                setMenuOpen(false);
                setEditingName(true);
              }}
            >
              <span className="drawer__icon">{state.emoji || '👤'}</span>
              <span className="drawer__label">
                {t.nickname.label}
                <small>{state.nickname || t.nickname.pickTitle}</small>
              </span>
            </button>

            <button
              type="button"
              className="drawer__row"
              onClick={() => {
                setMenuOpen(false);
                setShowLanguages(true);
              }}
            >
              <span className="drawer__icon">🌐</span>
              <span className="drawer__label">
                {t.language.title}
                <small>{LOCALES[locale].meta.name}</small>
              </span>
            </button>

            <button
              type="button"
              className="drawer__row"
              onClick={() => {
                setMenuOpen(false);
                setShowThemes(true);
              }}
            >
              <span className="drawer__icon">🎴</span>
              <span className="drawer__label">
                {t.theme.title}
                <small>{theme.name}</small>
              </span>
            </button>
          </nav>
        </div>
      )}

      {confirmLeave && (
        <LeaveConfirm
          onStay={() => setConfirmLeave(false)}
          onLeave={() => {
            setConfirmLeave(false);
            net.leaveRoom();
          }}
        />
      )}

      <Toasts toasts={state.toasts} onDismiss={(id) => net.dismissToast(id)} />
    </div>
  );
}
